import React, { useEffect, useState } from 'react'
import { BetmenPage, Tab } from '../../../common/components/BetmenPage'
import { useTranslation } from '../../../common/hooks/helper/useTranslation'
import { PatientList } from './components/PatientList'
import { AddButton } from '../../../common/components/AddButton/AddButton'
import { AddPatientModal } from './components/AddPatientModal'
import { usePatientsList } from '../../../common/hooks/usePatientsList'
import { PAGE_SIZE } from '../../../config'
import { PatientListFilters } from './components/PatientListFilters'
import { PatientStatus, UserType } from '../../../models/graphqlTypes'
import { useDebounce } from '../../../common/hooks'
import { useMe } from '../../../common/hooks/useMe'
import { PatientListFilter } from './types/patientFilter.types'
import omit from 'lodash/omit'
import { usePatientListTotal } from '../../../common/hooks/usePatientListTotal'

enum PatientListTabs {
  Active = 0,
  Removed = 1,
}

export const DoctorPatients: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation()
  const { data: { me } = {} } = useMe()
  const isAssistant = me?.userType === UserType.Assistant

  const doctorIds = isAssistant
    ? (me?.assistantDoctorIds ?? [])
    : [me?.profile?.id ?? '']

  const [selectedTabIndex, setSelectedTabIndex] = useState<PatientListTabs>(
    PatientListTabs.Active
  )

  const [filterInput, setFilterInput] = useState<PatientListFilter>({})
  const filterData: PatientListFilter = useDebounce(filterInput)

  const filters = {
    doctorIds:
      (filterData.doctorIds?.length ?? 0) > 0
        ? (filterData.doctorIds as string[])
        : doctorIds,
    filterData: {
      ...omit(filterData, 'doctorIds'),
      status:
        selectedTabIndex === PatientListTabs.Active
          ? PatientStatus.ACTIVE
          : PatientStatus.DISCHARGED,
    },
  }

  const { refetch, fetchMore, ...patientList } = usePatientsList({
    variables: {
      ...filters,
      paginationInput: {
        offset: 0,
        limit: PAGE_SIZE,
      },
    },
  })

  const fetchMorePatients = (offset: number) =>
    fetchMore({
      variables: {
        ...filters,
        paginationInput: {
          offset,
          limit: PAGE_SIZE,
        },
      },
    })

  useEffect(() => {
    refetch()
  }, [filterData, refetch, selectedTabIndex])

  const { total: totalActive, refetch: refetchActive } = usePatientListTotal({
    doctorIds,
    status: PatientStatus.ACTIVE,
  })
  const { total: totalDischarged, refetch: refetchDischarged } =
    usePatientListTotal({
      doctorIds,
      status: PatientStatus.DISCHARGED,
    })

  const refetchData = () => {
    refetch()
    refetchActive()
    refetchDischarged()
  }

  const tabs: Tab[] = [
    { label: `${t('doctor:patientList.current')} (${totalActive})` },
    { label: `${t('doctor:patientList.removed')} (${totalDischarged})` },
  ]

  const [isAddPatientsModalOpen, toggleIsAddPatientModalOpen] =
    useState<boolean>(false)

  const [isFilterPanelOpen, setFilterPanelOpen] = useState(true)

  return (
    <>
      <BetmenPage
        title={t('menu:doctor.patients')}
        tabs={tabs}
        onTabChange={setSelectedTabIndex}
        withoutPadding
        headerRightContent={
          <AddButton
            handleAdd={() => toggleIsAddPatientModalOpen(true)}
            buttonText={t(`patients:new`)}
          />
        }
        filters={
          <PatientListFilters
            isFilterPanelOpen={isFilterPanelOpen}
            setFilterPanelOpen={setFilterPanelOpen}
            filterInput={filterInput}
            setFilterInput={setFilterInput}
          />
        }
      >
        <PatientList
          filterInput={filterInput}
          setFilterInput={setFilterInput}
          isFilterPanelOpen={isFilterPanelOpen}
          setFilterPanelOpen={setFilterPanelOpen}
          {...patientList}
          fetchMore={fetchMorePatients}
          refetch={refetchData}
        />
      </BetmenPage>

      {isAddPatientsModalOpen && (
        <AddPatientModal
          patientList={patientList.data}
          isOpen={isAddPatientsModalOpen}
          toggleIsOpen={toggleIsAddPatientModalOpen}
        />
      )}
    </>
  )
}
